import React, { useContext, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { DateRangePicker } from "rsuite";
import { startOfDay, endOfDay, addDays, addMonths, subDays, startOfMonth, endOfMonth } from "date-fns";
import { convertDateAndReturn } from "../../scripts/utils";
import ChartData from "../../components/ChartData";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";

export default function IeloService({ setLink, serviceRef, setLinkType }) {
    const { apiUrl } = useContext(AuthContext);
    const { theme } = useTheme();
    const today = endOfDay(new Date(), 23);
    const yesterday = startOfDay(subDays(today, 1));
    const { afterToday, combine } = DateRangePicker;
    const [date, setDate] = useState([yesterday, today]);
    const [minDate] = useState(null);
    const { t } = useTranslation();

    const ranges = [
        {
            label: 'Aujourd\'hui',
            value: [startOfDay(new Date()), endOfDay(new Date())],
            placement: 'left'
        },
        {
            label: 'Hier',
            value: [startOfDay(addDays(new Date(), -1)), endOfDay(addDays(new Date(), -1))],
            placement: 'left'
        },
        {
            label: 'Les 7 derniers jours',
            value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())],
            placement: 'left'
        },
        {
            label: 'Les 30 derniers jours',
            value: [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())],
            placement: 'left'
        },
        {
            label: 'Ce mois-ci',
            value: [startOfMonth(new Date()), endOfDay(new Date())],
            placement: 'left'
        },
        {
            label: 'Le mois dernier',
            value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))],
            placement: 'left'
        }
    ];

    const getContrat = async () => {
        const res = await axios(`${apiUrl}ielo/graph/${serviceRef}/${convertDateAndReturn(date[0])}/${convertDateAndReturn(date[1])}`, {
            method: "get",
            withCredentials: true
        });

        return res.data;
    };

    const service = useQuery({
        queryKey: ["service_ielo", serviceRef, date],
        queryFn: getContrat,
        enabled: !!serviceRef
    });

    const prettyColors = {
        if_out_bytes: "#10B981",
        if_in_bytes: "#3B82F6",
        "95th percentile": "#EF4444",
        if_out_uncast_pkts: "#34D399",
        if_in_uncast_pkts: "#60A5FA",
        if_out_multicast_pkts: "#8B5CF6",
        if_in_multicast_pkts: "#6366F1",
        if_out_broadcast_pkts: "#F59E0B",
        if_in_broadcast_pkts: "#F97316",
        if_in_discards: "#FBBF24",
        if_in_errors: "#DC2626",
        if_out_discards: "#D97706",
        if_out_errors: "#991B1B"
    };

    const processGraphData = (graph) => {
        const allLabels = graph.measurements.map((measurement) => measurement.name);
        const customColors = {};

        graph.measurements.forEach((measurement) => {
            customColors[measurement.name] = prettyColors[measurement.name] || measurement.color || "#cccccc";
        });

        const formattedData = {};
        const groupedByTime = {};

        if (graph.values) {
            graph.values.forEach((value) => {
                if (!groupedByTime[value.datetime]) {
                    groupedByTime[value.datetime] = [];
                }

                let cleanValue = 0;

                if (value.value !== null && value.value !== undefined) {
                    const parsed = parseFloat(value.value);
                    if (!Number.isNaN(parsed)) {
                        cleanValue = Number(parsed.toFixed(2));
                    }
                }

                groupedByTime[value.datetime].push(cleanValue);
            });

            Object.keys(groupedByTime).forEach((datetime) => {
                formattedData[datetime] = {};
                graph.measurements.forEach((measurement, index) => {
                    const value = groupedByTime[datetime][index];
                    formattedData[datetime][measurement.name] = value !== undefined ? value : 0;
                });
            });
        }

        return { allLabels, formattedData, customColors };
    };

    let uniqueGraphs = [];

    if (service.data && Array.isArray(service.data.graphs)) {
        const seenNames = new Set();
        uniqueGraphs = service.data.graphs.filter((graph) => {
            const duplicate = seenNames.has(graph.name);
            seenNames.add(graph.name);
            return !duplicate;
        });
    }

    if (service.isLoading) {
        return <Loader />;
    }

    if (!service.data) {
        return <div className="no-data">{t("Aucune donnée disponible")}</div>;
    }

    return (
        <>
            <div className="cato-header-logo mb-2">
                <div className="ecp-container-content-header">
                    <div className="ecp-container-content-header-title">
                        <div className="page-title page-logo">
                            <h1 className="page-title">Liaison internet</h1>
                        </div>
                    </div>
                </div>
                <button className="cato-header-logo-button" onClick={() => { setLink(); setLinkType(); }}>
                    <FontAwesomeIcon icon={faChevronLeft} color={"white"} />
                    {t("Retour")}
                </button>
            </div>
            <section className="mb-2">
                <article className="cato-content-model-details-summary">
                    <span className="cato-content-model-details-summary-icon">
                        <FontAwesomeIcon icon={faInfoCircle} color={"#80c353ff"} />
                    </span>
                    <p>Service {serviceRef}</p>
                </article>
            </section>
            <div className="page-title page-logo">
                <DateRangePicker
                    ranges={ranges}
                    cleanable={false}
                    character=" / "
                    value={date}
                    onChange={setDate}
                    format="MM-dd-yyyy HH:mm"
                    defaultValue={[yesterday, today]}
                    shouldDisableDate={combine(afterToday(), (currentDate) => minDate && currentDate < startOfDay(new Date(minDate)))}
                    locale={{
                        sunday: t("Su"),
                        monday: t("Mo"),
                        tuesday: t("Tu"),
                        wednesday: t("We"),
                        thursday: t("Th"),
                        friday: t("Fr"),
                        saturday: t("Sa"),
                        hours: t("Hours"),
                    }}
                    placement="bottomStart"
                />
            </div>
            <section className="cato-content">
                {uniqueGraphs.length > 0 ? (
                    uniqueGraphs.map((graph, index) => {
                        const { allLabels, formattedData, customColors } = processGraphData(graph);
                        return (
                            <div key={`${graph.id}-${index}`} className="graph-container-ielo">
                                <ChartData
                                    key={`${graph.id}-${index}-${theme}-${date[0].getTime()}-${date[1].getTime()}`}
                                    theme={theme}
                                    titleChart={graph.name}
                                    dataForChart={formattedData}
                                    allLabels={allLabels}
                                    customColors={customColors}
                                    typeOfChart="line"
                                    addUnit={true}
                                    measurementUnit={graph.chart_type}
                                    maxHeight={400}
                                    automaticColors={false}
                                    interactionStatus={true}
                                    dynamicLegend={true}
                                    setNumberTicksX={20}
                                    tension={0.2}
                                    graphId="ielo"
                                />
                            </div>
                        );
                    })
                ) : (
                    <div className="no-data">{t("Aucune donnée disponible pour cette période")}</div>
                )}
            </section>
        </>
    );
}
